import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/seo";
import { FiGrid, FiPrinter, FiList } from "@/lib/icons";
import { StateSelector } from "./_components/StateSelector";
import { StampDutyForm } from "./_components/StampDutyForm";
import { StampDutyResultsDisplay } from "./_components/StampDutyResults";
import { useStampDutyCalculator } from "./_hooks/useStampDutyCalculator";
import { STATE_NAMES } from "./_types/calculator.types";

/**
 * Stamp Duty Calculator Page
 */
export function StampDutyCalculatorPage() {
  const { inputs, results, updateInput } = useStampDutyCalculator();

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <SEO
        title="Stamp Duty Calculator"
        description="Calculate stamp duty, government fees, and First Home Owner Grant for property purchases across all Australian states and territories."
        url="/calculators/stamp-duty"
      />

      {/* Hero Section */}
      <section className="bg-cream py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-[400px_1fr] gap-12 lg:gap-16 items-start">
            {/* Left Column - Info */}
            <div className="lg:sticky lg:top-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-accent/20 rounded-xl">
                  <FiGrid className="size-6 text-primary" />
                </div>
              </div>

              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6">
                Stamp Duty
              </h1>

              <p className="text-lg text-muted-foreground mb-8 max-w-md">
                Find out how much stamp duty you'll have to pay on a home.
              </p>

              <div className="border-t border-border pt-8 mt-8">
                <h2 className="font-serif text-2xl mb-4">
                  Send through a quick enquiry and we will be in touch.
                </h2>
                <p className="text-muted-foreground mb-6">
                  Whatever your circumstances, we will find the deal that's right for you.
                </p>
                <Button asChild className="btn-pill bg-accent text-accent-foreground hover:bg-accent/90">
                  <Link to="/contact">Let's have a chat</Link>
                </Button>
              </div>
            </div>

            {/* Right Column - Calculator */}
            <div className="bg-background rounded-3xl shadow-lg border border-border overflow-hidden">
              {/* Header */}
              <div className="px-8 py-6 border-b border-border bg-muted/30">
                <h2 className="font-serif text-xl md:text-2xl">
                  Stamp Duty Calculator 2026
                </h2>
              </div>

              {/* State Selector */}
              <StateSelector
                value={inputs.state}
                onChange={(state) => updateInput("state", state)}
              />

              {/* Form & Results */}
              <div className="p-8">
                <StampDutyForm inputs={inputs} onUpdate={updateInput} />

                {/* Results */}
                <StampDutyResultsDisplay results={results} inputs={inputs} />

                {/* Actions */}
                <div className="flex items-center gap-6 mt-10 pt-6 border-t border-border">
                  <button
                    onClick={handlePrint}
                    className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                    aria-label="Print results"
                  >
                    <FiPrinter className="size-4" />
                    Print
                  </button>
                  <button
                    onClick={() => {}}
                    className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                    aria-label="View rates and thresholds"
                  >
                    <FiList className="size-4" />
                    Rates and Thresholds
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-6">
            <p className="text-sm text-muted-foreground leading-relaxed">
              <strong>Note:</strong> The information provided by the calculator is intended to
              provide illustrative examples based on stated assumptions and your inputs.
              Calculations are meant as estimates only and it is advised that you consult
              with a mortgage broker about your specific circumstances. Rates shown are for{" "}
              {STATE_NAMES[inputs.state]} and are current as of January 2026. State government
              rates and thresholds may change without notice.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              <strong>Disclaimer:</strong> This page provides general information only and has been
              prepared without considering your objectives, financial situation or needs. We recommend
              that you consider whether it is appropriate for your circumstances and your full financial
              situation will need to be reviewed prior to acceptance of any offer or product. It does
              not constitute legal, tax or financial advice and you should always seek professional
              advice in relation to your individual circumstances.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
